import React from 'react';
import { Check, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useLanguage } from 'shared/context/LanguageContext';
import { cn } from 'shared/lib/utils';

interface PremiumStepperProps {
  /** Current logical step in the full onboarding flow. */
  current: number;
  /** Highest step the user has reached (gates forward jumps). */
  maxReached: number;
  /** Total logical steps in the flow (used for the sr-only announcement). */
  total: number;
  /** First step of the visible track (defaults to the first key). */
  trackFromStep?: number;
  /** Last step of the visible track (defaults to trackFromStep + keys - 1). */
  trackToStep?: number;
  /** i18n keys for each node in the visible track. */
  stepKeys?: string[];
  /** i18n base path for step labels. */
  stepLabelNs?: string;
  /** i18n base path for stepper a11y strings. */
  stepperLabelNs?: string;
  /** Called when the user clicks a step they have already reached. */
  onStepSelect?: (step: number) => void;
  /** Compact mode: drop the header descender and shrink the container. */
  compact?: boolean;
}

const PremiumStepper: React.FC<PremiumStepperProps> = ({
  current,
  maxReached,
  total,
  trackFromStep = 1,
  trackToStep,
  stepKeys,
  stepLabelNs = 'steps',
  stepperLabelNs = 'onboarding.stepper',
  onStepSelect,
  compact = false,
}) => {
  const { t } = useTranslation();
  const { dir } = useLanguage();

  const keys = stepKeys ?? [];
  const effectiveToStep = trackToStep ?? trackFromStep + keys.length - 1;

  if (current < trackFromStep || current > effectiveToStep) {
    return null;
  }

  const inTrackIndex = current - trackFromStep;
  const visibleCount = effectiveToStep - trackFromStep + 1;
  const isRtl = dir === 'rtl';

  return (
    <nav
      aria-label={t(`${stepperLabelNs}.ariaLabel`) as string}
      className="w-full"
    >
      <ol className={cn('flex w-full', compact ? 'pb-1' : 'pb-2')}>
        {keys.map((k, idx) => {
          const stepNumber = idx + trackFromStep;
          const isComplete = idx < inTrackIndex;
          const isActive = idx === inTrackIndex;
          const isReached = stepNumber <= maxReached;
          const isLocked = !isReached;
          const isClickable = isReached && !isActive && !!onStepSelect;
          const label = t(`${stepLabelNs}.${k}`);

          return (
            <li
              key={k}
              className="relative flex min-w-0 flex-1 flex-col items-center"
              aria-current={isActive ? 'step' : undefined}
            >
              {idx < keys.length - 1 ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    'absolute top-5 h-px',
                    idx < inTrackIndex ? 'bg-[#bf0035]/30' : 'bg-gray-200',
                  )}
                  style={
                    isRtl
                      ? {
                          right: 'calc(50% + 20px)',
                          left: 'calc(-50% + 20px)',
                        }
                      : {
                          left: 'calc(50% + 20px)',
                          right: 'calc(-50% + 20px)',
                        }
                  }
                />
              ) : null}

              <button
                type="button"
                onClick={isClickable ? () => onStepSelect?.(stepNumber) : undefined}
                disabled={!isClickable}
                aria-label={label as string}
                className={cn(
                  'relative z-10 grid h-10 w-10 place-items-center rounded-full border text-sm font-bold transition-colors',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#bf0035] focus-visible:ring-offset-2',
                  isActive &&
                    'border-[#bf0035] bg-[#bf0035] text-white',
                  !isActive && isComplete &&
                    'border-[#bf0035]/20 bg-[#bf0035]/[0.08] text-[#bf0035]',
                  !isActive && !isComplete && isReached &&
                    'border-gray-300 bg-white text-gray-600 hover:border-[#bf0035] hover:bg-[#bf0035]/[0.05] hover:text-[#bf0035]',
                  isLocked &&
                    'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-300',
                  isClickable && 'cursor-pointer',
                )}
              >
                {isComplete ? (
                  <Check size={16} strokeWidth={2.5} />
                ) : isLocked ? (
                  <Lock size={14} strokeWidth={2.25} />
                ) : (
                  idx + 1
                )}
              </button>

              <span
                className={cn(
                  // Two lines of the larger type instead of an ellipsis: with
                  // seven nodes the labels no longer fit on one line on mobile.
                  'mt-2 max-w-full px-1 text-center text-xs font-medium leading-tight sm:text-base',
                  'line-clamp-2',
                  isActive && 'font-semibold text-[#bf0035]',
                  !isActive && isComplete && 'text-[#bf0035]/75',
                  !isActive && !isComplete && isReached && 'text-gray-600',
                  isLocked && 'text-gray-400',
                )}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ol>

      <span className="sr-only">
        {t('common.stepOf', { current: inTrackIndex + 1, total: visibleCount })} / {total}
      </span>
    </nav>
  );
};

export default PremiumStepper;
